import React from 'react';
import styles from './Devices.module.scss';

interface BatteryIndicatorProps {
  battery: number | null;
}

/** Matches rescue-landing getBatteryIcon(): █ = filled, ░ = empty */
function getBatteryIcon(pct: number): string {
  if (pct > 75) {
    return '\u2588\u2588\u2588\u2588';
  }
  if (pct > 50) {
    return '\u2588\u2588\u2588\u2591';
  }
  if (pct > 25) {
    return '\u2588\u2588\u2591\u2591';
  }
  if (pct > 0) {
    return '\u2588\u2591\u2591\u2591';
  }
  return '\u2591\u2591\u2591\u2591';
}

/**
 * The bar and percentage on one line. It reads inside the Device cell rather than in a
 * column of its own, where it is already on its own line — stacking the two would cost a
 * second one for nothing.
 */
export default function BatteryIndicator({ battery }: BatteryIndicatorProps) {
  if (battery === null) {
    return <span className={styles.textMuted}>{'\u2014'}</span>;
  }

  let colorClass: string;
  if (battery > 50) {
    colorClass = styles.batteryOk;
  } else if (battery > 20) {
    colorClass = styles.batteryMid;
  } else {
    colorClass = styles.batteryLow;
  }

  // Labelled, because in the Device cell the reading has no column header to name it.
  // `role="img"` is what makes the label count: ARIA forbids aria-label on the implicit
  // `generic` role of a bare span, so conforming screen readers drop it and announce the
  // bare percentage with no indication of what it measures.
  return (
    <span className={`${colorClass} ${styles.battery}`} role="img" aria-label={`Battery ${battery}%`}>
      <span aria-hidden="true">{getBatteryIcon(battery)}</span>
      <span className={styles.batteryPct}>{battery}%</span>
    </span>
  );
}
